import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { fileUrl } from "../../lib/fileUrl";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { User, Mail, Phone, Shield, MapPin, Plus, Pencil, Trash2, Save, X, Eye, EyeOff, ShoppingCart, BookOpen, BookOpenCheck, CalendarDays, Sun, Clock, ChefHat, Briefcase, Calendar, Smartphone, RotateCcw, Lock, FileText, Download, Upload } from "lucide-react";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { ToggleSwitch as Switch } from "../../components/ui/toggle-switch";
import StoricoDipendenteCard from "../../components/anagrafica/StoricoDipendenteCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RUOLI = [
  { value: "admin", label: "Amministratore", color: "bg-primary/15 text-primary" },
  { value: "cucina", label: "Operatore Cucina", color: "bg-blue-500/15 text-blue-600" },
  { value: "sala", label: "Operatore Sala", color: "bg-purple-500/15 text-purple-600" },
  { value: "laboratorio", label: "Operatore Laboratorio", color: "bg-amber-500/15 text-amber-600" }
];

const TIPI_CONTRATTO = [
  { value: "non_definito",  label: "Non definito",    ore: 0,  ferie: 0,  lordo_sep: 1.44, lordo_busta: 1.32, costo_sep: 1.88, costo_busta: 1.72 },
  { value: "full_time",     label: "Full Time",        ore: 40, ferie: 26, lordo_sep: 1.44, lordo_busta: 1.32, costo_sep: 1.88, costo_busta: 1.72 },
  { value: "part_time_30",  label: "Part Time 30h",    ore: 30, ferie: 26, lordo_sep: 1.36, lordo_busta: 1.26, costo_sep: 1.77, costo_busta: 1.64 },
  { value: "part_time_24",  label: "Part Time 24h",    ore: 24, ferie: 26, lordo_sep: 1.30, lordo_busta: 1.22, costo_sep: 1.69, costo_busta: 1.58 },
  { value: "part_time_20",  label: "Part Time 20h",    ore: 20, ferie: 26, lordo_sep: 1.28, lordo_busta: 1.20, costo_sep: 1.66, costo_busta: 1.56 },
  { value: "apprendistato", label: "Apprendistato",    ore: 40, ferie: 26, lordo_sep: 1.24, lordo_busta: 1.16, costo_sep: 1.46, costo_busta: 1.37 },
  { value: "stagionale",    label: "Stagionale",       ore: 40, ferie: 0,  lordo_sep: 1.44, lordo_busta: 1.32, costo_sep: 1.82, costo_busta: 1.67 },
  { value: "chiamata",      label: "A Chiamata",       ore: 0,  ferie: 0,  lordo_sep: 1.28, lordo_busta: 1.20, costo_sep: 1.66, costo_busta: 1.56 },
];

// TUTTE le funzionalità assegnabili agli operatori
const FUNZIONALITA_INFO = {
  turni: { label: "Gestione Turni", icon: CalendarDays, color: "text-indigo-500", requiredPackage: "Gestione Personale" },
  creazione_ricette: { label: "Gestione Ricette", icon: ChefHat, color: "text-orange-500", requiredPackage: "Gestione Acquisti" },
  gestione_menu: { label: "Gestione Menu di Vendita", icon: BookOpenCheck, color: "text-violet-500", requiredPackage: "Gestione Acquisti" },
  ordini_fornitori: { label: "Gestione ordini", icon: ShoppingCart, color: "text-emerald-500", requiredPackage: "Gestione Acquisti" },
  approva_richieste: { label: "Approva richieste", icon: Shield, color: "text-cyan-500", requiredPackage: "Gestione Personale" },
};

// Mappatura features DB → funzionalità operatore  
const PACKAGE_FEATURES_MAP = {
  "Gestione Acquisti": ["creazione_ricette", "gestione_menu", "ordini_fornitori"],
  "Gestione Personale": ["turni", "approva_richieste"],
};

const Utenti = () => {
  const { getAuthHeaders, isAdmin, localeCorrente } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [utenti, setUtenti] = useState([]);
  const [locali, setLocali] = useState([]);
  const [operatoreContratti, setOperatoreContratti] = useState([]);
  const [dispositivi, setDispositivi] = useState({});
  const [resetDeviceTarget, setResetDeviceTarget] = useState(null); // { utenteId, nome }
  const [resettingDevice, setResettingDevice] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingContrattoFile, setPendingContrattoFile] = useState(null); // file in attesa per nuovo operatore
  const [dialogTab, setDialogTab] = useState("anagrafica");
  const [ownedPackageNames, setOwnedPackageNames] = useState(new Set());
  const [viewMode, setViewMode] = useState("attivi"); // "attivi" | "storico"
  const [storicoDipendenti, setStoricoDipendenti] = useState([]);
  const [form, setForm] = useState({
    password: "",
    nome: "",
    email: "",
    telefono: "",
    ruolo: "cucina",
    locali_ids: [],
    funzionalita: [],
    // Dati contrattuali
    tipo_contratto: "full_time",
    ore_settimanali: 40,
    giorni_ferie_annuali: 26,
    giorni_ferie_residui: 26,
    data_assunzione: "",
    data_fine_contratto: "",
    note_contratto: "",
    stipendio_mensile_netto: null,
    tredicesima_in_busta: false
  });

  useEffect(() => {
    fetchData();
    fetchOwnedPackages();
  }, []);

  const fetchData = async () => {
    try {
      const [utentiRes, localiRes, storicoRes] = await Promise.all([
        axios.get(`${API}/utenti`, { headers: getAuthHeaders() }),
        axios.get(`${API}/locali`, { headers: getAuthHeaders() }),
        axios.get(`${API}/utenti-disattivati`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
      ]);
      setUtenti(utentiRes.data);
      setLocali(localiRes.data);
      setStoricoDipendenti(storicoRes.data || []);
      // Fetch device info for each non-admin user
      const ops = utentiRes.data.filter(u => u.ruolo !== "admin" && u.ruolo !== "superadmin");
      const devMap = {};
      await Promise.all(ops.map(async (u) => {
        try {
          const res = await axios.get(`${API}/auth/dispositivo-operatore/${u.id}`, { headers: getAuthHeaders() });
          if (res.data && res.data.device_token) devMap[u.id] = res.data;
        } catch { /* nessun dispositivo */ }
      }));
      setDispositivi(devMap);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnedPackages = async () => {
    try {
      const res = await axios.get(`${API}/billing/pricing-summary`, { headers: getAuthHeaders() });
      const owned = new Set();
      (res.data.pacchetti_principali || []).forEach(p => {
        if (p.owned) owned.add(p.nome);
      });
      // If account is gratuito, all packages are available
      if (res.data.is_gratuito) {
        Object.keys(PACKAGE_FEATURES_MAP).forEach(n => owned.add(n));
      }
      setOwnedPackageNames(owned);
    } catch {}
  };

  const isFuncLocked = (funcKey) => {
    const info = FUNZIONALITA_INFO[funcKey];
    if (!info?.requiredPackage) return false;
    return !ownedPackageNames.has(info.requiredPackage);
  };


  const handleResetDispositivo = (utenteId, nome) => {
    setResetDeviceTarget({ utenteId, nome });
  };

  const confirmResetDispositivo = async () => {
    if (!resetDeviceTarget) return;
    const { utenteId, nome } = resetDeviceTarget;
    setResettingDevice(true);
    try {
      const res = await axios.delete(`${API}/auth/dispositivo-operatore/${utenteId}`, { headers: getAuthHeaders() });
      void res;
      setDispositivi(prev => { const n = { ...prev }; delete n[utenteId]; return n; });
      toast.success(`Dispositivo di ${nome} resettato. L'operatore può ora accedere da un nuovo dispositivo.`);
      setResetDeviceTarget(null);
    } catch (err) {
      console.error("Reset dispositivo error:", err);
      const msg = err?.response?.data?.detail || err?.message || "Errore sconosciuto";
      toast.error(`Errore reset dispositivo: ${msg}`);
    } finally {
      setResettingDevice(false);
    }
  };

  const openDialog = (item = null) => {
    setDialogTab("anagrafica");
    // Se c'è un solo locale, assegnarlo di default e bloccarlo
    const defaultLocali = locali.length === 1 ? [locali[0].id] : [];

    if (item) {
      setEditItem(item);
      setForm({
        password: "",
        nome: item.nome || "",
        email: item.email || "",
        telefono: item.telefono || "",
        ruolo: item.ruolo || "cucina",
        locali_ids: locali.length === 1 ? [locali[0].id] : (item.locali_ids || []),
        funzionalita: item.funzionalita || [],
        tipo_contratto: item.tipo_contratto || "non_definito",
        ore_settimanali: item.ore_settimanali || 40,
        giorni_ferie_annuali: item.giorni_ferie_annuali || 26,
        giorni_ferie_residui: item.giorni_ferie_residui ?? item.giorni_ferie_annuali ?? 26,
        data_assunzione: item.data_assunzione || "",
        data_fine_contratto: item.data_fine_contratto || "",
        note_contratto: item.note_contratto || "",
        stipendio_mensile_netto: item.stipendio_mensile_netto ?? null,
        tredicesima_in_busta: item.tredicesima_in_busta || false
      });
    } else {
      setEditItem(null);
      setForm({
        password: "",
        nome: "",
        email: "",
        telefono: "",
        ruolo: "cucina",
        locali_ids: defaultLocali,
        funzionalita: [],
        tipo_contratto: "non_definito",
        ore_settimanali: 0,
        giorni_ferie_annuali: 0,
        giorni_ferie_residui: 0,
        data_assunzione: "",
        data_fine_contratto: "",
        note_contratto: "",
        stipendio_mensile_netto: null,
        tredicesima_in_busta: false
      });
    }
    setDialogOpen(true);
    setPendingContrattoFile(null);
  };

  const handleTipoContrattoChange = (value) => {
    const tipo = TIPI_CONTRATTO.find(t => t.value === value);
    if (tipo) {
      setForm(prev => ({
        ...prev,
        tipo_contratto: value,
        ore_settimanali: tipo.ore,
        giorni_ferie_annuali: tipo.ferie,
        giorni_ferie_residui: prev.giorni_ferie_residui || tipo.ferie
      }));
    }
  };

  const toggleLocale = (localeId) => {
    setForm(prev => ({
      ...prev,
      locali_ids: prev.locali_ids.includes(localeId)
        ? prev.locali_ids.filter(id => id !== localeId)
        : [...prev.locali_ids, localeId]
    }));
  };

  // Carica contratti quando si apre il dialog in modifica
  useEffect(() => {
    if (dialogOpen && editItem?.id) {
      axios.get(`${API}/contratti?dipendente_id=${editItem.id}`, { headers: getAuthHeaders() })
        .then(r => setOperatoreContratti(r.data || []))
        .catch(() => setOperatoreContratti([]));
    } else if (!dialogOpen) {
      setOperatoreContratti([]);
    }
  }, [dialogOpen, editItem?.id]); // eslint-disable-line

  const handleContrattoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editItem?.id) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.post(`${API}/contratti`, {
          dipendente_id: editItem.id,
          nome: file.name,
          file_data: reader.result,
          file_name: file.name
        }, { headers: getAuthHeaders() });
        toast.success("Contratto caricato");
        const cRes = await axios.get(`${API}/contratti?dipendente_id=${editItem.id}`, { headers: getAuthHeaders() });
        setOperatoreContratti(cRes.data || []);
      } catch { toast.error("Errore nel caricamento del contratto"); }
    };
    reader.readAsDataURL(file);
  };

  const handleContrattoDelete = async (contrattoId) => {
    try {
      await axios.delete(`${API}/contratti/${contrattoId}`, { headers: getAuthHeaders() });
      toast.success("Contratto rimosso");
      setOperatoreContratti(prev => prev.filter(c => c.id !== contrattoId));
    } catch { toast.error("Errore nella rimozione del contratto"); }
  };

  const toggleFunzionalita = (func) => {
    setForm(prev => {
      const isAdding = !prev.funzionalita.includes(func);
      let newFunz = isAdding
        ? [...prev.funzionalita, func]
        : prev.funzionalita.filter(f => f !== func);

      // "Gestione ordini" include automaticamente acquisti e fornitori
      if (func === "ordini_fornitori") {
        if (isAdding) {
          if (!newFunz.includes("acquisti")) newFunz.push("acquisti");
          if (!newFunz.includes("fornitori")) newFunz.push("fornitori");
        } else {
          newFunz = newFunz.filter(f => f !== "acquisti" && f !== "fornitori");
        }
      }

      return { ...prev, funzionalita: newFunz };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    if (!form.nome.trim()) { toast.error("Nome completo obbligatorio"); return; }
    if (!form.ruolo) { toast.error("Ruolo obbligatorio"); return; }

    // Cellulare obbligatorio per tutti (lo usano per il login)
    const telDigits = (form.telefono || "").replace(/\D/g, "");
    if (!telDigits) {
      toast.error("Il numero di cellulare è obbligatorio: serve per il login dell'operatore.");
      return;
    }
    if (telDigits.length < 6) {
      toast.error("Numero di cellulare non valido (servono almeno 6 cifre).");
      return;
    }
    // Email: lowercase e validazione formato (se fornita)
    const emailNorm = (form.email || "").trim().toLowerCase();
    if (emailNorm && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailNorm)) {
      toast.error("Email non valida");
      return;
    }

    if (!editItem && !form.password) { toast.error("Password obbligatoria per nuovi utenti"); return; }
    if (!editItem && form.password.length < 4) { toast.error("Il PIN/Password deve essere di almeno 4 caratteri"); return; }
    if (form.ruolo !== "admin" && locali.length > 1 && form.locali_ids.length === 0) {
      toast.error("Seleziona almeno un locale");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: form.nome,
        email: emailNorm,
        telefono: telDigits,
        ruolo: form.ruolo,
        locali_ids: form.locali_ids,
        funzionalita: form.ruolo === "admin" ? [] : form.funzionalita,
        // Dati contrattuali
        tipo_contratto: form.tipo_contratto,
        ore_settimanali: form.ore_settimanali,
        giorni_ferie_annuali: form.giorni_ferie_annuali,
        giorni_ferie_residui: form.giorni_ferie_residui,
        data_assunzione: form.data_assunzione || null,
        data_fine_contratto: form.data_fine_contratto || null,
        note_contratto: form.note_contratto || null,
        stipendio_mensile_netto: form.stipendio_mensile_netto ?? null,
        tredicesima_in_busta: form.tredicesima_in_busta || false
      };

      if (editItem) {
        if (form.password) {
          payload.password = form.password;
        }
        await axios.put(`${API}/utenti/${editItem.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Operatore aggiornato");
      } else {
        const res = await axios.post(`${API}/utenti`, {
          ...payload,
          password: form.password
        }, { headers: getAuthHeaders() });
        // Se c'è un contratto PDF in attesa, lo carico subito con l'ID appena creato
        if (pendingContrattoFile && res.data?.id) {
          const newId = res.data.id;
          const reader = new FileReader();
          await new Promise((resolve, reject) => {
            reader.onload = async () => {
              try {
                await axios.post(`${API}/contratti`, {
                  dipendente_id: newId,
                  nome: pendingContrattoFile.name,
                  file_data: reader.result,
                  file_name: pendingContrattoFile.name
                }, { headers: getAuthHeaders() });
                resolve();
              } catch (err) { reject(err); }
            };
            reader.onerror = reject;
            reader.readAsDataURL(pendingContrattoFile);
          });
          toast.success("Operatore creato con contratto allegato");
        } else {
          toast.success("Operatore creato");
        }
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (!await confirm("Disattivare questo operatore?\n\nLo storico (presenze, turni, buste paga) resta intatto.\nL'operatore sparirà dalla lista attiva ma sarà visibile in Storico Dipendenti.")) return;

    try {
      await axios.delete(`${API}/utenti/${id}`, { headers: getAuthHeaders() });
      toast.success("Operatore disattivato. Lo storico è preservato.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella disattivazione");
    }
  };

  const handleRiattiva = async (id) => {
    if (!isAdmin) return;
    if (!await confirm("Riattivare questo operatore?")) return;
    try {
      await axios.post(`${API}/utenti/${id}/riattiva`, {}, { headers: getAuthHeaders() });
      toast.success("Operatore riattivato");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  const handleHardDelete = async (id, nome) => {
    if (!isAdmin) return;
    if (!await confirm(`ATTENZIONE: eliminazione definitiva di "${nome}".\n\nQuesta azione è IRREVERSIBILE.\nLe presenze e i turni storici resteranno con uno snapshot del nome.\n\nProcedere?`)) return;
    try {
      await axios.delete(`${API}/utenti/${id}/hard`, { headers: getAuthHeaders() });
      toast.success("Operatore eliminato definitivamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const getRuoloInfo = (ruolo) => RUOLI.find(r => r.value === ruolo) || RUOLI[1];
  const getLocaleNome = (id) => locali.find(l => l.id === id)?.nome || id;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operatori-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Operatori</h1>
          <p className="text-muted-foreground text-sm mt-1">Gestisci gli operatori e le loro funzionalità</p>
        </div>
        {isAdmin && viewMode === "attivi" && (
          <Button onClick={() => openDialog()} className="bg-primary hover:bg-primary/90 text-white" data-testid="btn-nuovo-operatore">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Operatore
          </Button>
        )}
      </div>

      {/* Tabs Attivi / Storico */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setViewMode("attivi")}
          className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 -mb-px ${
            viewMode === "attivi" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="tab-attivi"
        >
          Attivi ({utenti.filter(u => u.ruolo !== "admin" && u.ruolo !== "superadmin").length})
        </button>
        <button
          onClick={() => setViewMode("storico")}
          className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 -mb-px ${
            viewMode === "storico" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="tab-storico"
        >
          Storico ({storicoDipendenti.length})
        </button>
      </div>

      {/* Lista Storico Dipendenti */}
      {viewMode === "storico" && (
        <div className="space-y-3" data-testid="lista-storico">
          {storicoDipendenti.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun dipendente disattivato</p>
              <p className="text-xs mt-2">Gli operatori disattivati appariranno qui con tutto il loro storico.</p>
            </div>
          ) : (
            storicoDipendenti.map((u) => (
              <StoricoDipendenteCard
                key={u.id}
                utente={u}
                isAdmin={isAdmin}
                getAuthHeaders={getAuthHeaders}
                onRiattiva={handleRiattiva}
                onHardDelete={handleHardDelete}
              />
            ))
          )}
        </div>
      )}

      {/* Lista Utenti Attivi */}
      {viewMode === "attivi" && (
      <div className="space-y-3">
        {utenti.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nessun operatore registrato</p>
          </div>
        ) : (
          utenti.map((utente) => {
            const ruoloInfo = getRuoloInfo(utente.ruolo);
            return (
              <div
                key={utente.id}
                className="bg-card backdrop-blur-sm border border-border rounded-2xl p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-6 w-6 text-foreground/70" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-foreground">{utente.nome || utente.email || utente.telefono}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ruoloInfo.color}`}>
                          {ruoloInfo.label}
                        </span>
                        {!utente.attivo && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                            Disattivato
                          </span>
                        )}
                      </div>

                      {/* Contatti */}
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                        {utente.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {utente.email}
                          </span>
                        )}
                        {utente.telefono && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {utente.telefono}
                          </span>
                        )}
                      </div>

                      {/* Locali assegnati */}
                      {utente.ruolo !== "admin" && utente.locali_ids?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {utente.locali_ids.map(lid => (
                            <span key={lid} className="px-2 py-0.5 bg-muted rounded text-xs text-white/60">
                              <MapPin className="h-3 w-3 inline mr-1" />
                              {getLocaleNome(lid)}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Funzionalità assegnate */}
                      {utente.ruolo !== "admin" && utente.funzionalita?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {utente.funzionalita.map(func => {
                            const info = FUNZIONALITA_INFO[func];
                            if (!info) return null;
                            const Icon = info.icon;
                            return (
                              <span key={func} className={`px-2 py-0.5 bg-card rounded text-xs flex items-center gap-1 ${info.color}`}>
                                <Icon className="h-3 w-3" />
                                {info.label}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Info Contratto */}
                      {utente.ruolo !== "admin" && utente.tipo_contratto && (
                        <div className="flex flex-wrap gap-2 mt-2 text-xs">
                          <span className="px-2 py-0.5 bg-blue-500/15 text-blue-500 rounded flex items-center gap-1">
                            <Briefcase className="h-3 w-3" />
                            {TIPI_CONTRATTO.find(t => t.value === utente.tipo_contratto)?.label || utente.tipo_contratto}
                          </span>
                          {utente.ore_settimanali > 0 && (
                            <span className="px-2 py-0.5 bg-muted rounded text-foreground/70">
                              {utente.ore_settimanali}h/sett
                            </span>
                          )}
                          {utente.giorni_ferie_residui !== undefined && (
                            <span className="px-2 py-0.5 bg-amber-500/15 text-amber-500 rounded">
                              {utente.giorni_ferie_residui} gg ferie
                            </span>
                          )}
                        </div>
                      )}

                      {/* Numero di telefono */}
                      {utente.ruolo !== "admin" && (
                        <div className="flex items-center gap-1 mt-2">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {utente.telefono || "Nessun numero registrato"}
                          </span>
                        </div>
                      )}

                      {/* Stato dispositivo vincolato */}
                      {utente.ruolo !== "admin" && (
                        <div className="flex items-center gap-2 mt-2">
                          <Smartphone className="h-3 w-3 text-muted-foreground" />
                          {dispositivi[utente.id] ? (
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-green-500/15 text-green-500 rounded text-xs font-medium" data-testid={`device-bound-${utente.id}`}>
                                Dispositivo vincolato
                              </span>
                              {isAdmin && (
                                <button
                                  onClick={() => handleResetDispositivo(utente.id, utente.nome || utente.email || utente.telefono)}
                                  className="px-2 py-0.5 bg-amber-500/15 text-amber-500 rounded text-xs font-medium hover:bg-amber-500/25 transition-colors flex items-center gap-1"
                                  data-testid={`reset-device-${utente.id}`}
                                >
                                  <RotateCcw className="h-3 w-3" /> Reset
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="px-2 py-0.5 bg-muted rounded text-xs text-muted-foreground" data-testid={`device-unbound-${utente.id}`}>
                              Nessun dispositivo
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => openDialog(utente)} className="h-9 w-9 text-muted-foreground hover:text-foreground">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(utente.id)} className="h-9 w-9 text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      )}

      {/* Dialog Crea/Modifica */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              {editItem ? "Modifica Operatore" : "Nuovo Operatore"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <Tabs value={dialogTab} onValueChange={setDialogTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4 h-11 bg-muted p-1 rounded-lg gap-1">
                <TabsTrigger 
                  value="anagrafica" 
                  className="flex items-center justify-center gap-1 sm:gap-2 rounded-md font-medium transition-all text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground"
                >
                  <User className="h-4 w-4" />
                  Anagrafica
                </TabsTrigger>
                <TabsTrigger 
                  value="contratto" 
                  className="flex items-center justify-center gap-1 sm:gap-2 rounded-md font-medium transition-all text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground"
                >
                  <Briefcase className="h-4 w-4" />
                  Contratto
                </TabsTrigger>
              </TabsList>
              
              {/* TAB ANAGRAFICA */}
              <TabsContent value="anagrafica" className="space-y-4 mt-0">
                {/* Nome */}
                <div className="space-y-2">
                  <Label className="text-foreground/70">
                    Nome completo <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={form.nome}
                    onChange={(e) => setForm(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="es. Mario Rossi"
                    className="bg-card border-border text-foreground"
                  />
                </div>

                {/* DATI DI ACCESSO — Telefono + Password in linea */}
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
                  <div className="flex items-center gap-2 -mt-1">
                    <Lock className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold text-foreground">Dati per il login</span>
                    <span className="text-xs text-muted-foreground">— l'operatore userà questi per accedere</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-foreground/70 text-xs uppercase tracking-wide">
                        Cellulare <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={15}
                        value={form.telefono}
                        onChange={(e) => setForm(prev => ({ ...prev, telefono: e.target.value.replace(/\D/g, '') }))}
                        placeholder="es. 3491234567"
                        className="bg-card border-border text-foreground"
                        required
                        data-testid="utente-telefono-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground/70 text-xs uppercase tracking-wide">
                        {editItem ? "Password" : <>Password <span className="text-red-500">*</span></>}
                      </Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={form.password}
                          onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                          placeholder={editItem ? "Lascia vuoto per non modificare" : "Inserisci password"}
                          className="bg-card border-border text-foreground pr-10"
                          data-testid="password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Email (opzionale, per comunicazioni) */}
                <div className="space-y-2">
                  <Label className="text-foreground/70">
                    Email <span className="text-xs text-muted-foreground ml-1">(opzionale, per comunicazioni)</span>
                  </Label>
                  <Input
                    type="text"
                    inputMode="email"
                    autoComplete="nope"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    name="operatore-email-field"
                    readOnly
                    onFocus={(e) => e.target.removeAttribute('readOnly')}
                    value={form.email}
                    onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value.toLowerCase().trim() }))}
                    placeholder="email@..."
                    className="bg-card border-border text-foreground lowercase"
                    data-testid="utente-email-input"
                  />
                </div>

                {/* Ruolo */}
                <div className="space-y-2">
                  <Label className="text-foreground/70">
                    Ruolo <span className="text-red-500">*</span>
                  </Label>
                  <Select value={form.ruolo} onValueChange={(v) => setForm(prev => ({ ...prev, ruolo: v }))}>
                    <SelectTrigger className="bg-card border-border text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {RUOLI.map(r => (
                        <SelectItem key={r.value} value={r.value} className="text-foreground hover:bg-muted">
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Locali (solo per non-admin) */}
                {form.ruolo !== "admin" && (
                  <div className="space-y-2">
                    <Label className="text-foreground/70">
                      Locali assegnati
                      {locali.length > 1 && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    <div className="space-y-2 bg-card rounded-xl p-3 border border-border max-h-32 overflow-y-auto">
                      {locali.length === 0 ? (
                        <p className="text-muted-foreground text-sm">Nessun locale disponibile</p>
                      ) : locali.length === 1 ? (
                        // Locale singolo: pre-selezionato e bloccato
                        <label className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 cursor-not-allowed">
                          <Checkbox
                            checked={true}
                            disabled
                            className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary opacity-60"
                          />
                          <span className="text-foreground">{locali[0].nome}</span>
                          <span className="text-xs text-muted-foreground ml-auto">Assegnato di default</span>
                        </label>
                      ) : (
                        // Multi-locale: almeno 1 obbligatorio
                        locali.map(locale => (
                          <label key={locale.id} className="flex items-center gap-3 p-2 hover:bg-muted rounded-lg cursor-pointer">
                            <Checkbox
                              checked={form.locali_ids.includes(locale.id)}
                              onCheckedChange={() => toggleLocale(locale.id)}
                              className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <span className="text-foreground">{locale.nome}</span>
                          </label>
                        ))
                      )}
                    </div>
                    {locali.length > 1 && form.locali_ids.length === 0 && (
                      <p className="text-xs text-red-500">Seleziona almeno un locale</p>
                    )}
                  </div>
                )}

                {/* Funzionalità (solo per non-admin) */}
                {form.ruolo !== "admin" && (
                  <div className="space-y-2">
                    <Label className="text-foreground/70">Funzionalità abilitate</Label>
                    <div className="grid grid-cols-2 gap-2 bg-card rounded-xl p-3 border border-border max-h-48 overflow-y-auto">
                      <TooltipProvider delayDuration={200}>
                        {Object.entries(FUNZIONALITA_INFO).map(([key, info]) => {
                          const Icon = info.icon;
                          const locked = isFuncLocked(key);
                          const content = (
                            <label
                              key={key}
                              className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${locked ? 'opacity-45 cursor-not-allowed bg-muted/30' : 'cursor-pointer hover:bg-muted'}`}
                              data-testid={`func-${key}`}
                            >
                              {locked ? (
                                <div className="h-4 w-4 flex items-center justify-center flex-shrink-0">
                                  <Lock className="h-3.5 w-3.5 text-gray-400" />
                                </div>
                              ) : (
                                <Checkbox
                                  checked={form.funzionalita.includes(key)}
                                  onCheckedChange={() => toggleFunzionalita(key)}
                                  className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                />
                              )}
                              <Icon className={`h-4 w-4 ${locked ? 'text-gray-400' : info.color}`} />
                              <span className={`text-sm ${locked ? 'text-gray-400' : 'text-foreground'}`}>{info.label}</span>
                              {locked && <Lock className="h-3 w-3 text-gray-300 ml-auto flex-shrink-0" />}
                            </label>
                          );
                          if (locked) {
                            return (
                              <Tooltip key={key}>
                                <TooltipTrigger asChild>{content}</TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px] text-center">
                                  <p className="text-xs">Attiva il pacchetto <strong>{info.requiredPackage}</strong> per abilitare questa funzione</p>
                                </TooltipContent>
                              </Tooltip>
                            );
                          }
                          return content;
                        })}
                      </TooltipProvider>
                    </div>
                  </div>
                )}
              </TabsContent>
              
              {/* TAB CONTRATTO */}
              <TabsContent value="contratto" className="space-y-4 mt-0">
                {/* Tipo Contratto */}
                <div className="space-y-2">
                  <Label className="text-foreground/70 flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Tipo Contratto
                  </Label>
                  <Select value={form.tipo_contratto} onValueChange={handleTipoContrattoChange}>
                    <SelectTrigger className="bg-card border-border text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {TIPI_CONTRATTO.map(t => (
                        <SelectItem key={t.value} value={t.value} className="text-foreground hover:bg-muted">
                          {t.value === "non_definito" ? t.label : `${t.label} (${t.ore}h/sett)`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Stipendio netto + 13a + calcolo lordo */}
                <div className="space-y-3 rounded-xl border border-border bg-muted/10 p-3">
                  <div className="space-y-2">
                    <Label className="text-foreground/70">Stipendio mensile netto (€)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="50"
                      value={form.stipendio_mensile_netto ?? ""}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        stipendio_mensile_netto: e.target.value ? parseFloat(e.target.value) : null
                      }))}
                      placeholder="Es. 1200"
                      className="bg-card border-border text-foreground"
                      data-testid="input-stipendio-mensile-netto"
                    />
                  </div>
                  {/* Checkbox 13a */}
                  <div className="flex items-center gap-2.5">
                    <Checkbox
                      id="tredicesima-in-busta"
                      checked={form.tredicesima_in_busta || false}
                      onCheckedChange={(v) => setForm(prev => ({ ...prev, tredicesima_in_busta: !!v }))}
                      data-testid="checkbox-tredicesima-in-busta"
                    />
                    <Label htmlFor="tredicesima-in-busta" className="text-sm text-foreground/80 cursor-pointer">
                      13ª inclusa nella busta paga mensile
                    </Label>
                  </div>
                  {/* Calcolo lordo stimato */}
                  {form.stipendio_mensile_netto > 0 && form.tipo_contratto !== "non_definito" && (() => {
                    const t = TIPI_CONTRATTO.find(c => c.value === form.tipo_contratto);
                    if (!t) return null;
                    const k = form.tredicesima_in_busta;
                    const lordoMens = form.stipendio_mensile_netto * (k ? t.lordo_busta : t.lordo_sep);
                    const costoMens = form.stipendio_mensile_netto * (k ? t.costo_busta : t.costo_sep);
                    return (
                      <div className="text-xs bg-blue-500/8 border border-blue-500/20 rounded-lg px-3 py-2 space-y-1">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Lordo mensile stimato:</span>
                          <span className="font-semibold text-foreground">€ {lordoMens.toFixed(0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Costo aziendale mensile:</span>
                          <span className="font-semibold text-foreground">€ {costoMens.toFixed(0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Labor Cost annuale:</span>
                          <span className="font-semibold text-foreground">€ {(costoMens * 12).toFixed(0)}</span>
                        </div>
                        <p className="text-muted-foreground/60 mt-1">
                          {k ? "13ª spalmata sul mensile." : "13ª come mensilità aggiuntiva."} Stime CCNL Turismo — confrontare con CDL.
                        </p>
                      </div>
                    );
                  })()}
                </div>

                {/* Ore e Ferie */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-foreground/70 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Ore Settimanali
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="60"
                      value={form.ore_settimanali}
                      onChange={(e) => setForm(prev => ({ ...prev, ore_settimanali: parseInt(e.target.value) || 0 }))}
                      className="bg-card border-border text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground/70 flex items-center gap-2">
                      <Sun className="h-4 w-4" />
                      Ferie Annuali (gg)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      value={form.giorni_ferie_annuali}
                      onChange={(e) => setForm(prev => ({ ...prev, giorni_ferie_annuali: parseInt(e.target.value) || 0 }))}
                      className="bg-card border-border text-foreground"
                    />
                  </div>
                </div>

                {/* Ferie Residue */}
                <div className="space-y-2">
                  <Label className="text-foreground/70">Ferie Residue (gg)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.giorni_ferie_residui}
                    onChange={(e) => setForm(prev => ({ ...prev, giorni_ferie_residui: parseFloat(e.target.value) || 0 }))}
                    className="bg-card border-border text-foreground"
                  />
                  <p className="text-muted-foreground text-xs">Giorni di ferie ancora disponibili per l'anno corrente</p>
                </div>

                {/* Date Contratto */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-foreground/70 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Data Assunzione
                    </Label>
                    <DateInput value={form.data_assunzione} onChange={(v) => setForm(prev => ({ ...prev, data_assunzione: v }))} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground/70">Scadenza Contratto</Label>
                    <DateInput value={form.data_fine_contratto} onChange={(v) => setForm(prev => ({ ...prev, data_fine_contratto: v }))} />
                    <p className="text-muted-foreground text-xs">Solo per contratti a termine</p>
                  </div>
                </div>

                {/* Note */}
                <div className="space-y-2">
                  <Label className="text-foreground/70">Note Contratto</Label>
                  <Input
                    value={form.note_contratto}
                    onChange={(e) => setForm(prev => ({ ...prev, note_contratto: e.target.value }))}
                    placeholder="Eventuali note sul contratto..."
                    className="bg-card border-border text-foreground"
                  />
                </div>
                
                {/* Allegato Contratto PDF */}
                <div className="space-y-2">
                  <Label className="text-foreground/70 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Allegato Contratto (PDF)
                  </Label>
                  {!editItem ? (
                    <div className="space-y-2">
                      {pendingContrattoFile ? (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30 border border-border">
                          <FileText className="h-4 w-4 text-primary shrink-0" />
                          <span className="flex-1 text-sm text-foreground truncate">{pendingContrattoFile.name}</span>
                          <button onClick={() => setPendingContrattoFile(null)}
                            className="p-1.5 rounded-md hover:bg-red-500/10 text-red-500 transition-colors" title="Rimuovi">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : null}
                      <label className="flex items-center gap-2 p-2.5 rounded-lg border border-dashed border-border hover:border-primary/50 hover:bg-muted/30 cursor-pointer transition-colors">
                        <input type="file" className="hidden" accept=".pdf,image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10MB)"); return; }
                            setPendingContrattoFile(file);
                          }} />
                        <Upload className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {pendingContrattoFile ? "Sostituisci documento" : "Carica contratto (PDF)"}
                        </span>
                      </label>
                      <p className="text-xs text-muted-foreground">Il documento verrà allegato automaticamente al salvataggio.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Lista contratti esistenti */}
                      {operatoreContratti.length > 0 && (
                        <div className="space-y-1.5">
                          {operatoreContratti.map(c => (
                            <div key={c.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30 border border-border">
                              <FileText className="h-4 w-4 text-primary shrink-0" />
                              <span className="flex-1 text-sm text-foreground truncate">{c.file_nome || c.nome || "Contratto"}</span>
                              {c.file_url && (
                                <a href={fileUrl(c.file_url)} target="_blank" rel="noopener noreferrer"
                                  className="p-1.5 rounded-md hover:bg-primary/10 text-primary transition-colors"
                                  title="Scarica" data-testid={`download-contratto-${c.id}`}>
                                  <Download className="h-3.5 w-3.5" />
                                </a>
                              )}
                              <button onClick={() => handleContrattoDelete(c.id)}
                                className="p-1.5 rounded-md hover:bg-red-500/10 text-red-500 transition-colors"
                                title="Rimuovi" data-testid={`delete-contratto-${c.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Upload nuovo contratto */}
                      <label className="flex items-center gap-2 p-2.5 rounded-lg border border-dashed border-border hover:border-primary/50 hover:bg-muted/30 cursor-pointer transition-colors"
                        data-testid="upload-contratto-label">
                        <input type="file" className="hidden" accept=".pdf,image/*"
                          onChange={handleContrattoUpload} data-testid="upload-contratto-input" />
                        <Upload className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {operatoreContratti.length > 0 ? "Aggiungi altro documento" : "Carica contratto (PDF)"}
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Riepilogo visuale */}
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <h4 className="font-medium text-foreground mb-2">Riepilogo</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div className="text-muted-foreground">Contratto:</div>
                    <div className="text-foreground font-medium">{TIPI_CONTRATTO.find(t => t.value === form.tipo_contratto)?.label || "-"}</div>
                    <div className="text-muted-foreground">Ore/settimana:</div>
                    <div className="text-foreground font-medium">{form.ore_settimanali}h</div>
                    <div className="text-muted-foreground">Ferie residue:</div>
                    <div className="text-foreground font-medium">{form.giorni_ferie_residui} giorni</div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-border text-foreground hover:bg-muted">
                Annulla
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold">
                {saving ? "Salvataggio..." : (editItem ? "Salva" : "Crea")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Dialog conferma reset dispositivo ── */}
      <Dialog open={!!resetDeviceTarget} onOpenChange={(o) => !o && setResetDeviceTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reset-device-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-500" />
              Reset dispositivo
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground">
              Vuoi davvero resettare il dispositivo vincolato a <b className="text-foreground">{resetDeviceTarget?.nome}</b>?
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Dopo il reset l'operatore potrà accedere da un nuovo dispositivo (telefono, tablet o PC).
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setResetDeviceTarget(null)} disabled={resettingDevice} data-testid="cancel-reset-device">
              Annulla
            </Button>
            <Button type="button" onClick={confirmResetDispositivo} disabled={resettingDevice}
              className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="confirm-reset-device">
              {resettingDevice ? "Reset in corso..." : "Conferma reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Utenti;
